import React, { createContext, useContext, ReactNode } from 'react';
import { API_BASE_URL } from '../services/api';

interface User {
  _id?: string;
  id?: string;
  name?: string;
  email?: string;
  role: string;
  department?: string;
  jobTitle?: string;
  permissions?: string[];
  preferences?: {
    theme?: string;
    notifications?: {
      email?: boolean;
      inApp?: boolean;
    };
  };
  lastActive?: string;
  createdAt?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error?: string;
  isDevMode?: boolean;
  login: (email: string, password: string) => Promise<{ mfaRequired?: boolean; userId?: string }>;
  logout: () => void;
  register: (name: string, email: string, password: string, department?: string, jobTitle?: string) => Promise<{
    message?: string;
    data?: {
      accountStatus: string;
    };
  }>;
  updateUser: (userData: Partial<User>) => Promise<void>;
  autoLogin: (userType: 'admin' | 'data-steward' | 'user') => Promise<void>;
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: false,
  login: async () => ({ mfaRequired: false }),
  logout: () => {},
  register: async () => ({ data: { accountStatus: 'pending' } }),
  updateUser: async () => {},
  autoLogin: async () => {}
});

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = React.useState<User | null>(null);
  const [loading, setLoading] = React.useState(true);

  // Check for existing session on mount
  React.useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          // Fetch current user from API
          const response = await fetch(`${API_BASE_URL}/auth/me`, {
            headers: {
              'Authorization': `Bearer ${token}`
            },
            credentials: 'include'
          });
          
          if (response.ok) {
            const data = await response.json();
            setUser(data.user || data.data);
          } else {
            localStorage.removeItem('token');
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    const loginUrl = `${API_BASE_URL}/auth/login`;
    console.log('🔐 Login URL:', loginUrl);
    console.log('🔐 API_BASE_URL:', API_BASE_URL);
    
    try {
      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
        credentials: 'include'
      });

      console.log('🔐 Response status:', response.status);
      console.log('🔐 Response ok:', response.ok);

      const data = await response.json();
      console.log('🔐 Response data:', data);
      
      if (response.ok && !data.mfaRequired) {
        if (data.token) {
          localStorage.setItem('token', data.token);
        }
        setUser(data.user || data.data);
      }

      return data;
    } catch (error) {
      console.error('🔐 Login fetch error:', error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    window.location.href = '/';
  };

  const register = async (name: string, email: string, password: string, department?: string, jobTitle?: string) => {
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, password, department, jobTitle }),
      credentials: 'include'
    });

    return await response.json();
  };

  const updateUser = async (userData: Partial<User>) => {
    setUser(prev => prev ? { ...prev, ...userData } : null);
  };

  const autoLogin = async (userType: 'admin' | 'data-steward' | 'user') => {
    // Auto-login for development
    const mockUser: User = {
      _id: '1',
      name: userType === 'admin' ? 'Admin User' : userType === 'data-steward' ? 'Data Steward' : 'Regular User',
      email: `${userType}@example.com`,
      role: userType
    };
    setUser(mockUser);
  };

  return React.createElement(
    AuthContext.Provider,
    { 
      value: { 
        user, 
        loading, 
        login, 
        logout,
        register,
        updateUser,
        autoLogin
      } 
    },
    children
  );
};

export const useAuth = () => useContext(AuthContext);
